'use client'
import { useState, useEffect, useRef, useCallback } from "react"
import posthog from 'posthog-js'

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import ContactForm from "./ContactForm";
import RoamDropInButtonCalendar from "../embeds/RoamDropInButtonCalendar";
import ContactFollowUpModal from "../ui/contact-followup-modal";

const FOLLOWUP_DELAY_MS = 20000;
const SESSION_KEY = "kk-contact-followup-shown";

export default function HybridContactForm() {
    const [activeTab, setActiveTab] = useState("contact-us")
    const [isFollowUpOpen, setIsFollowUpOpen] = useState(false)
    const formInteractedRef = useRef(false)
    const modalShownRef = useRef(false)
    const timerRef = useRef(null)
    const formContainerRef = useRef(null)

    const openFollowUp = useCallback(() => {
      if (modalShownRef.current) return;
      // Don't show if user already switched to the "Talk To An Expert" tab
      if (activeTab === "talk-to-us") return;
      // Only show once per session
      if (typeof sessionStorage !== "undefined" && sessionStorage.getItem(SESSION_KEY)) return;

      modalShownRef.current = true;
      setIsFollowUpOpen(true);
      if (typeof sessionStorage !== "undefined") {
        sessionStorage.setItem(SESSION_KEY, "1");
      }
    }, [activeTab]);

    const closeFollowUp = useCallback(() => {
      setIsFollowUpOpen(false);
    }, []);

    // Time-based trigger: show modal after delay
    useEffect(() => {
      // Skip if already shown this session
      if (typeof sessionStorage !== "undefined" && sessionStorage.getItem(SESSION_KEY)) return;

      timerRef.current = setTimeout(() => {
        openFollowUp();
      }, FOLLOWUP_DELAY_MS);

      return () => {
        if (timerRef.current) clearTimeout(timerRef.current);
      };
    }, [openFollowUp]);

    // Exit-intent trigger: user interacted with form then clicked outside
    useEffect(() => {
      const formContainer = formContainerRef.current;
      if (!formContainer) return;

      const handleFocusIn = () => {
        formInteractedRef.current = true;
        // Once user interacts with the form, clear the timer so we
        // use the exit-intent trigger instead
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
      };

      const handleClickOutside = (e) => {
        if (
          formInteractedRef.current &&
          !modalShownRef.current &&
          formContainer &&
          !formContainer.contains(e.target)
        ) {
          openFollowUp();
        }
      };

      formContainer.addEventListener("focusin", handleFocusIn);
      document.addEventListener("mousedown", handleClickOutside);

      return () => {
        formContainer.removeEventListener("focusin", handleFocusIn);
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [openFollowUp]);

    // If user switches to "Talk To An Expert" tab, dismiss modal logic
    useEffect(() => {
      if (activeTab === "talk-to-us") {
        // They chose to talk to an expert already — cancel any pending triggers
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
        modalShownRef.current = true;
        if (typeof sessionStorage !== "undefined") {
          sessionStorage.setItem(SESSION_KEY, "1");
        }
      }
    }, [activeTab]);

    const handleTabChange = useCallback((value) => {
      setActiveTab(value);
      if (value === "talk-to-us") {
        posthog.capture('contact_expert_tab_selected');
      }
    }, []);

    return (
      <>
        <Card className="w-full" ref={formContainerRef}>
          <CardHeader>
            <CardTitle>Contact Us</CardTitle>
            <CardDescription>
              Let&apos;s talk about your project.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-4">
                <TabsTrigger value="contact-us">Send A Message</TabsTrigger>
                <TabsTrigger value="talk-to-us">Talk To An Expert</TabsTrigger>
              </TabsList>
              <TabsContent value="contact-us">
                <div className="space-y-4 py-2">
                  <ContactForm />
                </div>
              </TabsContent>
              <TabsContent key={activeTab} value="talk-to-us">
                <div className="flex space-y-4 py-2 h-[865px] w-full">
                  {activeTab === "talk-to-us" && <RoamDropInButtonCalendar isActive={activeTab === "talk-to-us"} />}
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        <ContactFollowUpModal isOpen={isFollowUpOpen} onClose={closeFollowUp} />
      </>
    )
  }
